import { useEffect } from 'react';
import Navbar from '../../home/components/Navbar';
import Footer from '../../home/components/Footer';
import { SEO } from '../../../utils/seo';

export default function MaintenancePage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

  const schema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "serviceType": "Security Systems Maintenance",
    "provider": {
      "@type": "LocalBusiness",
      "name": "LEWIS SECURITY, RISK & COMPLIANCE SOLUTIONS",
      "address": {
        "@type": "PostalAddress",
        "addressLocality": "North Wales",
        "addressRegion": "Wales",
        "addressCountry": "GB"
      }
    },
    "areaServed": {
      "@type": "Place",
      "name": "North Wales"
    },
    "description": "Reliable ongoing maintenance contracts with rapid response times, regular system checks, and 24/7 emergency support to keep your security systems operational.",
    "offers": {
      "@type": "Offer",
      "availability": "https://schema.org/InStock"
    },
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "Maintenance Services",
      "itemListElement": [
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Regular Inspections",
            "description": "Scheduled preventative maintenance visits to inspect, test, and service all security equipment"
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Emergency Repairs",
            "description": "24/7 emergency callout service for urgent repairs and system failures"
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "System Updates",
            "description": "Regular software updates and firmware upgrades to keep systems current"
          }
        }
      ]
    }
  };

  return (
    <>
      <SEO
        title="Security Systems Maintenance North Wales - 24/7 Support - LEWIS SECURITY"
        description="Reliable ongoing maintenance contracts with rapid response times, regular system checks, and 24/7 emergency support to keep your security systems operational. Professional maintenance services in North Wales."
        keywords="security maintenance North Wales, 24/7 emergency support, system maintenance, preventative maintenance, security system repairs, maintenance contracts"
        schema={schema}
      />
      <div className="min-h-screen bg-white">
        <Navbar />
        
        {/* Hero Section */}
        <section className="relative pt-32 pb-20 bg-gradient-to-br from-gray-900 via-teal-900 to-gray-800">
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/40"></div>
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center text-white">
              <div className="w-20 h-20 bg-red-600 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <i className="ri-tools-line text-5xl text-white"></i>
              </div>
              <h1 className="text-5xl sm:text-6xl font-bold mb-6 font-poppins">Maintenance & Support</h1>
              <p className="text-xl text-gray-200 max-w-3xl mx-auto font-inter">
                Reliable ongoing maintenance contracts with rapid response times, regular system checks, and 24/7 emergency support to keep your security systems operational
              </p>
            </div>
          </div>
        </section>

        {/* Main Content */}
        <section className="py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-16">
              <div>
                <h2 className="text-3xl font-bold text-gray-900 mb-6 font-poppins">Keeping Your Systems Running</h2>
                <p className="text-gray-600 mb-6 leading-relaxed font-inter">
                  Security and fire safety systems require regular maintenance to remain effective and compliant. LEWIS SECURITY provides comprehensive maintenance and support services ensuring your systems operate at peak performance 24/7, giving you complete peace of mind.
                </p>
                <p className="text-gray-600 mb-6 leading-relaxed font-inter">
                  Our experienced engineers provide scheduled maintenance, emergency repairs, and technical support across North Wales. With rapid response times and genuine parts, we minimize downtime and ensure your premises remain protected at all times.
                </p>
              </div>
              <div className="relative h-96 rounded-2xl overflow-hidden shadow-2xl">
                <img 
                  src="https://public.readdy.ai/ai/img_res/82251abb11fd67d8fbc9a126d6be61ab.jpg"
                  alt="Maintenance Services"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>

            {/* Services Grid */}
            <div className="mb-16">
              <h2 className="text-3xl font-bold text-gray-900 mb-10 text-center font-poppins">Our Maintenance Services</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div className="bg-white rounded-xl p-8 shadow-lg border border-gray-100">
                  <div className="w-14 h-14 bg-red-50 rounded-lg flex items-center justify-center mb-4">
                    <i className="ri-calendar-check-line text-3xl text-red-600"></i>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3 font-poppins">Regular Inspections</h3>
                  <p className="text-gray-600 mb-4 font-inter">
                    Scheduled preventative maintenance visits to inspect, test, and service all security and fire safety equipment. Our comprehensive checks ensure systems remain compliant and operational.
                  </p>
                  <ul className="space-y-2">
                    <li className="flex items-start text-sm text-gray-700 font-inter">
                      <i className="ri-check-line text-red-600 mr-2 text-lg mt-0.5"></i>
                      <span>Quarterly system inspections</span>
                    </li>
                    <li className="flex items-start text-sm text-gray-700 font-inter">
                      <i className="ri-check-line text-red-600 mr-2 text-lg mt-0.5"></i>
                      <span>Annual compliance testing</span>
                    </li>
                    <li className="flex items-start text-sm text-gray-700 font-inter">
                      <i className="ri-check-line text-red-600 mr-2 text-lg mt-0.5"></i>
                      <span>Component testing and cleaning</span>
                    </li>
                    <li className="flex items-start text-sm text-gray-700 font-inter">
                      <i className="ri-check-line text-red-600 mr-2 text-lg mt-0.5"></i>
                      <span>Detailed inspection reports</span>
                    </li>
                  </ul>
                </div>

                <div className="bg-white rounded-xl p-8 shadow-lg border border-gray-100">
                  <div className="w-14 h-14 bg-red-50 rounded-lg flex items-center justify-center mb-4">
                    <i className="ri-alarm-warning-line text-3xl text-red-600"></i>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3 font-poppins">Emergency Repairs</h3>
                  <p className="text-gray-600 mb-4 font-inter">
                    24/7 emergency callout service for urgent repairs and system failures. Our engineers respond rapidly to restore your security systems and minimize vulnerability periods.
                  </p>
                  <ul className="space-y-2">
                    <li className="flex items-start text-sm text-gray-700 font-inter">
                      <i className="ri-check-line text-red-600 mr-2 text-lg mt-0.5"></i>
                      <span>24/7 emergency response</span>
                    </li>
                    <li className="flex items-start text-sm text-gray-700 font-inter">
                      <i className="ri-check-line text-red-600 mr-2 text-lg mt-0.5"></i>
                      <span>Rapid fault diagnosis</span>
                    </li>
                    <li className="flex items-start text-sm text-gray-700 font-inter">
                      <i className="ri-check-line text-red-600 mr-2 text-lg mt-0.5"></i>
                      <span>On-site repairs when possible</span>
                    </li>
                    <li className="flex items-start text-sm text-gray-700 font-inter">
                      <i className="ri-check-line text-red-600 mr-2 text-lg mt-0.5"></i>
                      <span>Genuine replacement parts</span>
                    </li>
                  </ul>
                </div>

                <div className="bg-white rounded-xl p-8 shadow-lg border border-gray-100">
                  <div className="w-14 h-14 bg-red-50 rounded-lg flex items-center justify-center mb-4">
                    <i className="ri-refresh-line text-3xl text-red-600"></i>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3 font-poppins">System Updates</h3>
                  <p className="text-gray-600 mb-4 font-inter">
                    Regular software updates and firmware upgrades to keep your systems current with the latest features and security patches. We ensure your technology stays ahead of emerging threats.
                  </p>
                  <ul className="space-y-2">
                    <li className="flex items-start text-sm text-gray-700 font-inter">
                      <i className="ri-check-line text-red-600 mr-2 text-lg mt-0.5"></i>
                      <span>Software and firmware updates</span>
                    </li>
                    <li className="flex items-start text-sm text-gray-700 font-inter">
                      <i className="ri-check-line text-red-600 mr-2 text-lg mt-0.5"></i>
                      <span>Security patch management</span>
                    </li>
                    <li className="flex items-start text-sm text-gray-700 font-inter">
                      <i className="ri-check-line text-red-600 mr-2 text-lg mt-0.5"></i>
                      <span>Feature enhancements</span>
                    </li>
                    <li className="flex items-start text-sm text-gray-700 font-inter">
                      <i className="ri-check-line text-red-600 mr-2 text-lg mt-0.5"></i>
                      <span>System optimization</span>
                    </li>
                  </ul>
                </div>

                <div className="bg-white rounded-xl p-8 shadow-lg border border-gray-100">
                  <div className="w-14 h-14 bg-red-50 rounded-lg flex items-center justify-center mb-4">
                    <i className="ri-customer-service-2-line text-3xl text-red-600"></i>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3 font-poppins">Technical Support</h3>
                  <p className="text-gray-600 mb-4 font-inter">
                    Dedicated technical support helpline for troubleshooting, user guidance, and system queries. Our knowledgeable team provides prompt assistance to resolve issues quickly.
                  </p>
                  <ul className="space-y-2">
                    <li className="flex items-start text-sm text-gray-700 font-inter">
                      <i className="ri-check-line text-red-600 mr-2 text-lg mt-0.5"></i>
                      <span>Telephone support helpline</span>
                    </li>
                    <li className="flex items-start text-sm text-gray-700 font-inter">
                      <i className="ri-check-line text-red-600 mr-2 text-lg mt-0.5"></i>
                      <span>Remote diagnostics</span>
                    </li>
                    <li className="flex items-start text-sm text-gray-700 font-inter">
                      <i className="ri-check-line text-red-600 mr-2 text-lg mt-0.5"></i>
                      <span>User training and guidance</span>
                    </li>
                    <li className="flex items-start text-sm text-gray-700 font-inter">
                      <i className="ri-check-line text-red-600 mr-2 text-lg mt-0.5"></i>
                      <span>System configuration assistance</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Maintenance Plans */}
            <div className="bg-gradient-to-br from-red-50 to-gray-50 rounded-2xl p-12 mb-16">
              <h2 className="text-3xl font-bold text-gray-900 mb-10 text-center font-poppins">Flexible Maintenance Plans</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                <div className="bg-white rounded-xl p-8 shadow-lg">
                  <div className="text-center mb-6">
                    <h3 className="text-2xl font-bold text-gray-900 mb-2 font-poppins">Essential</h3>
                    <p className="text-sm text-gray-600 font-inter">Basic maintenance coverage</p>
                  </div>
                  <ul className="space-y-3 mb-8">
                    <li className="flex items-start text-sm text-gray-700 font-inter">
                      <i className="ri-check-line text-red-600 mr-2 text-lg mt-0.5"></i>
                      <span>Annual system inspection</span>
                    </li>
                    <li className="flex items-start text-sm text-gray-700 font-inter">
                      <i className="ri-check-line text-red-600 mr-2 text-lg mt-0.5"></i>
                      <span>Compliance certification</span>
                    </li>
                    <li className="flex items-start text-sm text-gray-700 font-inter">
                      <i className="ri-check-line text-red-600 mr-2 text-lg mt-0.5"></i>
                      <span>Emergency callout (charged)</span>
                    </li>
                    <li className="flex items-start text-sm text-gray-700 font-inter">
                      <i className="ri-check-line text-red-600 mr-2 text-lg mt-0.5"></i>
                      <span>Telephone support</span>
                    </li>
                  </ul>
                  <a 
                    href="/#contact" 
                    className="block text-center px-6 py-3 bg-gray-900 text-white font-semibold rounded-lg hover:bg-gray-800 transition-colors duration-300 whitespace-nowrap cursor-pointer"
                  >
                    Get Quote
                  </a>
                </div>

                <div className="bg-white rounded-xl p-8 shadow-lg border-2 border-red-600 relative">
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <span className="bg-red-600 text-white px-4 py-1 rounded-full text-xs font-bold">POPULAR</span>
                  </div>
                  <div className="text-center mb-6">
                    <h3 className="text-2xl font-bold text-gray-900 mb-2 font-poppins">Professional</h3>
                    <p className="text-sm text-gray-600 font-inter">Comprehensive protection</p>
                  </div>
                  <ul className="space-y-3 mb-8">
                    <li className="flex items-start text-sm text-gray-700 font-inter">
                      <i className="ri-check-line text-red-600 mr-2 text-lg mt-0.5"></i>
                      <span>Quarterly inspections</span>
                    </li>
                    <li className="flex items-start text-sm text-gray-700 font-inter">
                      <i className="ri-check-line text-red-600 mr-2 text-lg mt-0.5"></i>
                      <span>Priority emergency response</span>
                    </li>
                    <li className="flex items-start text-sm text-gray-700 font-inter">
                      <i className="ri-check-line text-red-600 mr-2 text-lg mt-0.5"></i>
                      <span>Free callouts and labor</span>
                    </li>
                    <li className="flex items-start text-sm text-gray-700 font-inter">
                      <i className="ri-check-line text-red-600 mr-2 text-lg mt-0.5"></i>
                      <span>Software updates included</span>
                    </li>
                    <li className="flex items-start text-sm text-gray-700 font-inter">
                      <i className="ri-check-line text-red-600 mr-2 text-lg mt-0.5"></i>
                      <span>24/7 technical support</span>
                    </li>
                  </ul>
                  <a 
                    href="/#contact" 
                    className="block text-center px-6 py-3 bg-red-600 text-white font-semibold rounded-lg hover:bg-red-700 transition-colors duration-300 whitespace-nowrap cursor-pointer"
                  >
                    Get Quote
                  </a>
                </div>

                <div className="bg-white rounded-xl p-8 shadow-lg">
                  <div className="text-center mb-6">
                    <h3 className="text-2xl font-bold text-gray-900 mb-2 font-poppins">Premium</h3>
                    <p className="text-sm text-gray-600 font-inter">Complete peace of mind</p>
                  </div>
                  <ul className="space-y-3 mb-8">
                    <li className="flex items-start text-sm text-gray-700 font-inter">
                      <i className="ri-check-line text-red-600 mr-2 text-lg mt-0.5"></i>
                      <span>Monthly system checks</span>
                    </li>
                    <li className="flex items-start text-sm text-gray-700 font-inter">
                      <i className="ri-check-line text-red-600 mr-2 text-lg mt-0.5"></i>
                      <span>Guaranteed 4-hour response</span>
                    </li>
                    <li className="flex items-start text-sm text-gray-700 font-inter">
                      <i className="ri-check-line text-red-600 mr-2 text-lg mt-0.5"></i>
                      <span>All parts and labor included</span>
                    </li>
                    <li className="flex items-start text-sm text-gray-700 font-inter">
                      <i className="ri-check-line text-red-600 mr-2 text-lg mt-0.5"></i>
                      <span>Proactive system monitoring</span>
                    </li>
                    <li className="flex items-start text-sm text-gray-700 font-inter">
                      <i className="ri-check-line text-red-600 mr-2 text-lg mt-0.5"></i>
                      <span>Dedicated account manager</span>
                    </li>
                  </ul>
                  <a 
                    href="/#contact" 
                    className="block text-center px-6 py-3 bg-gray-900 text-white font-semibold rounded-lg hover:bg-gray-800 transition-colors duration-300 whitespace-nowrap cursor-pointer"
                  >
                    Get Quote
                  </a>
                </div>
              </div>
            </div>

            {/* Why Choose Our Maintenance */}
            <div className="mb-16">
              <h2 className="text-3xl font-bold text-gray-900 mb-10 text-center font-poppins">Why Choose Our Maintenance Services?</h2>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                <div className="text-center">
                  <div className="w-16 h-16 bg-red-600 rounded-full flex items-center justify-center mx-auto mb-4">
                    <i className="ri-time-line text-3xl text-white"></i>
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2 font-poppins">Rapid Response</h3>
                  <p className="text-sm text-gray-600 font-inter">Fast emergency response times across North Wales</p>
                </div>
                <div className="text-center">
                  <div className="w-16 h-16 bg-red-600 rounded-full flex items-center justify-center mx-auto mb-4">
                    <i className="ri-user-star-line text-3xl text-white"></i>
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2 font-poppins">Expert Engineers</h3>
                  <p className="text-sm text-gray-600 font-inter">Fully qualified and certified technicians</p>
                </div>
                <div className="text-center">
                  <div className="w-16 h-16 bg-red-600 rounded-full flex items-center justify-center mx-auto mb-4">
                    <i className="ri-shield-check-line text-3xl text-white"></i>
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2 font-poppins">Genuine Parts</h3>
                  <p className="text-sm text-gray-600 font-inter">Only manufacturer-approved components used</p>
                </div>
                <div className="text-center">
                  <div className="w-16 h-16 bg-red-600 rounded-full flex items-center justify-center mx-auto mb-4">
                    <i className="ri-file-list-3-line text-3xl text-white"></i>
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2 font-poppins">Full Documentation</h3>
                  <p className="text-sm text-gray-600 font-inter">Detailed reports and compliance certificates</p>
                </div>
              </div>
            </div>

            {/* CTA Section */}
            <div className="text-center bg-gray-900 rounded-2xl p-12">
              <h2 className="text-3xl font-bold text-white mb-4 font-poppins">Protect Your Investment</h2>
              <p className="text-gray-300 mb-8 max-w-2xl mx-auto font-inter">
                Don't wait for system failures. Contact us today to discuss a maintenance plan that keeps your security systems running perfectly
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a 
                  href="/#contact" 
                  className="inline-flex items-center justify-center px-8 py-4 bg-red-600 text-white font-semibold rounded-lg hover:bg-red-700 transition-colors duration-300 whitespace-nowrap cursor-pointer"
                >
                  Get Maintenance Quote
                  <i className="ri-arrow-right-line ml-2"></i>
                </a>
                <a 
                  href="tel:07834561015" 
                  className="inline-flex items-center justify-center px-8 py-4 bg-white text-gray-900 font-semibold rounded-lg hover:bg-gray-100 transition-colors duration-300 whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-phone-line mr-2"></i>
                  Call 07834 561015
                </a>
              </div>
            </div>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
}
